import type { MatchPoint } from '../../types';

/**
 * localStorage-backed offline queue for the live scoring pad (sc-338).
 *
 * Every point is appended here first, synced in the background, and only
 * removed from the "unsynced" count once the server confirms it. Each point
 * carries `client_id` + `seq` so a flush can be replayed after a crash/reload
 * without ever double-inserting (see matchPointsService.insertPoints).
 */

const STORAGE_PREFIX = 'hustli_tennis_match_queue_';

export interface QueuedPoint extends MatchPoint {
  synced: boolean;
}

interface QueueState {
  points: QueuedPoint[];
}

let storageAvailable: boolean | null = null;

/** Feature-detects localStorage (unavailable in some private-browsing modes). Cached after first check. */
export function isStorageAvailable(): boolean {
  if (storageAvailable !== null) return storageAvailable;
  try {
    const key = '__hustli_storage_test__';
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    storageAvailable = true;
  } catch {
    storageAvailable = false;
  }
  return storageAvailable;
}

/** In-memory fallback used when localStorage throws (private browsing, quota, etc.). */
const memoryFallback = new Map<string, QueueState>();

function key(gameId: string): string {
  return `${STORAGE_PREFIX}${gameId}`;
}

export function loadQueue(gameId: string): QueuedPoint[] {
  if (!isStorageAvailable()) {
    return memoryFallback.get(gameId)?.points ?? [];
  }
  try {
    const raw = window.localStorage.getItem(key(gameId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueueState;
    return Array.isArray(parsed.points) ? parsed.points : [];
  } catch {
    return [];
  }
}

function persist(gameId: string, points: QueuedPoint[]): void {
  if (!isStorageAvailable()) {
    memoryFallback.set(gameId, { points });
    return;
  }
  try {
    window.localStorage.setItem(key(gameId), JSON.stringify({ points }));
  } catch {
    // Quota exceeded or storage revoked mid-session — fall back to memory so
    // the match can keep going; the unsynced indicator covers the risk.
    memoryFallback.set(gameId, { points });
  }
}

export function appendPoint(gameId: string, point: MatchPoint): QueuedPoint[] {
  const points = [...loadQueue(gameId), { ...point, synced: false }];
  persist(gameId, points);
  return points;
}

/** Undo: pop the highest-seq point regardless of sync state. Returns it, or null if the queue was empty. */
export function popLastPoint(gameId: string): { points: QueuedPoint[]; removed: QueuedPoint | null } {
  const points = loadQueue(gameId);
  if (points.length === 0) return { points, removed: null };
  const removed = points[points.length - 1];
  const next = points.slice(0, -1);
  persist(gameId, next);
  return { points: next, removed };
}

/** Mark the given client_ids as synced (keeps them in the queue for replay-safety, but they no longer count as "unsynced"). */
export function markSynced(gameId: string, clientIds: string[]): QueuedPoint[] {
  const ids = new Set(clientIds);
  const points = loadQueue(gameId).map((p) => (ids.has(p.client_id) ? { ...p, synced: true } : p));
  persist(gameId, points);
  return points;
}

/**
 * Update the most recently appended point in place (the post-point detail
 * sheet's reason/shot tags). Forces `synced: false` even if the untagged
 * point already made it to the server — by the time a human picks a tag, the
 * initial flush has almost always already completed, so without this the
 * point never looks "dirty" again and flushQueue has nothing to re-push.
 */
export function updateLastPoint(gameId: string, updates: Partial<MatchPoint>): QueuedPoint[] {
  const points = loadQueue(gameId);
  if (points.length === 0) return points;
  const next = [...points];
  next[next.length - 1] = { ...next[next.length - 1], ...updates, synced: false };
  persist(gameId, next);
  return next;
}

export function clearQueue(gameId: string): void {
  memoryFallback.delete(gameId);
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.removeItem(key(gameId));
  } catch {
    // Nothing to do — storage already unavailable/unwritable.
  }
}

export function unsyncedCount(points: QueuedPoint[]): number {
  return points.filter((p) => !p.synced).length;
}

/** crypto.randomUUID is widely available in modern browsers, but keep a fallback (matches DrillForm's pattern). */
export function createClientId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `cid_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * Overwrite the whole local queue (e.g. after reconciling a seq conflict with
 * a concurrently-syncing second device — see matchPointsService.flushQueue).
 */
export function replaceQueue(gameId: string, points: QueuedPoint[]): QueuedPoint[] {
  persist(gameId, points);
  return points;
}
